import moment from 'moment'
import Vue from 'vue'
import Utils from '@/utils'
import { API, SportsKind, DataFreshnessKind } from '@/models/core'
import { Enum } from '@/helpers/enum'
import { CricketPlayer, CricketPlayerRole, CricketPlayerRoleOrder } from './player'

export const CricketMatchStatusKind = new Enum({
  Started: 'started',
  NotStarted: 'notstarted',
  Completed: 'completed'
})

export const CricketMatchFormat = new Enum({
  T10: 't10',
  T20: 't20',
  HundredBall: '100-ball',
  OneDay: 'one-day',
  Test: 'test'
})

const CricketMatchHours = {
  't10': 2,
  't20': 4,
  'one-day': 9,
  '100-ball': 4,
  'test': 9 + (24 * 4)
}

export class CricketMatchPlayer extends CricketPlayer {

}

export class CricketMatchPartnership {
  static createFromList (key, rawlist) {
    let items = []
    let maxRuns = 0

    rawlist.forEach(raw => {
      if (raw.player_a_runs > maxRuns) {
        maxRuns = raw.player_a_runs
      } else if (raw.player_b_runs > maxRuns) {
        maxRuns = raw.player_b_runs
      }
    })

    let prevPartnership = null
    for (let i = 0; i < rawlist.length; i++) {
      const raw = rawlist[i]
      let partnership = new CricketMatchPartnership()
      partnership = Object.assign(partnership, raw)

      let players = ['a', 'b']
      if (i > 0) {
        const prevPlayers = [prevPartnership.player1.key, prevPartnership.player2.key]
        let notOutPlayerIndex = prevPlayers.indexOf(partnership.player_a)
        if (notOutPlayerIndex > -1) {
          if (notOutPlayerIndex === 1) {
            players = ['b', 'a']
          }
        } else {
          notOutPlayerIndex = prevPlayers.indexOf(partnership.player_b)
          if (notOutPlayerIndex === 0) {
            players = ['b', 'a']
          }
        }
      }

      players.forEach((k, i) => {
        const runs = partnership[`player_${k}_runs`]
        const width = Math.floor(((runs / maxRuns) / 2) * 100)
        const left = 50 - width
        partnership[`player${i + 1}`] = {
          key: partnership[`player_${k}`],
          runs: runs,
          balls: partnership[`player_${k}_balls`],
          fours: partnership[`player_${k}_four`],
          sixes: partnership[`player_${k}_six`],
          width: width,
          balWidth: left
        }
      })

      items.push(partnership)
      prevPartnership = partnership
    }

    return items
  }
}

export class CricketMatchOverSummary {
  haveBatsmanFeature (feature) {
    for (let i = 0; i < this.batsman.length; i++) {
      if (this.batsman[i].batting[feature] > 0) {
        return true
      }
    }

    return false
  }

  haveBatsman (playerKey) {
    for (let i = 0; i < this.batsman.length; i++) {
      if (this.batsman[i].key === playerKey) {
        return true
      }
    }
  }

  haveBowler (playerKey) {
    for (let i = 0; i < this.bowler.length; i++) {
      if (this.bowler[i].key === playerKey) {
        return true
      }
    }
  }

  isOut (playerKey) {
    return this.wicket_players_keys.indexOf(playerKey) > -1
  }

  static createFromJson (raw) {
    let over = new CricketMatchOverSummary()
    over = Object.assign(over, raw)
    return over
  }
}

export class CricketPlayerFantasyCredit {
  static createFromJson (raw) {
    let credit = new CricketPlayerFantasyCredit()
    credit = Object.assign(credit, raw)
    credit.lastUpdated = moment(raw.last_updated_match_date)
    if (credit.credit_src === undefined) {
      credit.credit_src = 'roanuz'
    }
    return credit
  }
}

export class CricketPlayerFantasyPoint {
  static createFromJson (raw) {
    let point = new CricketPlayerFantasyPoint()
    point = Object.assign(point, raw)
    point.calculation = raw.calculation || {}
    point.lastUpdated = moment(raw.last_updated_match_date)
    return point
  }
}

export class CricketMatch {
  constructor () {
    this.sport = SportsKind.Cricket
  }

  get dataFreshness () {
    let freshness = DataFreshnessKind.Warm
    if (this.status.isStarted) {
      freshness = (this.isLiveNow) ? DataFreshnessKind.Live : DataFreshnessKind.Hot
    } else if (this.status.isCompleted) {
      if (this.approx_completed_ts) {
        const ts = moment.unix(this.approx_completed_ts)
        const delta = Math.abs(ts.diff(moment(), 'minutes'))
        if (delta < 15) {
          freshness = DataFreshnessKind.Hot
        }
      } else {
        freshness = DataFreshnessKind.Live
      }
    } else if (this.status.isNotStarted) {
      const delta = Math.abs(this.start_at.diff(moment(), 'minutes'))
      if (delta < 15) {
        freshness = DataFreshnessKind.Hot
      }
    }
    return freshness
  }

  get requiresLiveUpdate () {
    return this.dataFreshness.isLive || this.dataFreshness.isHot
  }

  get possibleCompleteDate () {
    if (this.status.isCompleted && this.approx_completed_ts) {
      return moment.unix(this.approx_completed_ts)
    }

    let hours = CricketMatchHours[this.format.value]
    return this.start_at.clone().add(hours, 'hours')
  }

  get shortName () {
    return this.short_name
  }

  get isLiveNow () {
    let live = this.status.isStarted
    if (this.status_overview && live && this.status_overview !== 'in_play') {
      live = false
    }

    return live
  }

  get liveStatusText () {
    if (this.isLiveNow) {
      return 'Live'
    } else if (this.status_overview) {
      return Utils.capitalise(this.status_overview)
    }

    return 'Live'
  }

  get standardMatchOvers () {
    if (this.format.isT10) {
      return 10
    } else if (this.format.isT20 || this.format.isHundredBall) {
      return 20
    } else if (this.format.isOneDay) {
      return 50
    }

    return null
  }

  get matchOvers () {
    const overs = this.match_overs
    if (overs !== null && overs !== this.standardMatchOvers) {
      return overs
    }

    return null
  }

  get haveTeamAPlayers () {
    return this.haveTeamPlayers('a')
  }

  get haveTeamAPlayingXI () {
    return this.haveTeamPlayers('a', true)
  }

  get haveTeamBPlayers () {
    return this.haveTeamPlayers('b')
  }

  get haveTeamBPlayingXI () {
    return this.haveTeamPlayers('b', true)
  }

  get havePlayers () {
    return this.haveTeamAPlayers || this.haveTeamBPlayers
  }

  get havePlayingXI () {
    return this.haveTeamAPlayingXI || this.haveTeamBPlayingXI
  }

  get haveTeamAXFactor () {
    return this.haveXFactorPlayers('a')
  }

  get haveTeamBXFactor () {
    return this.haveXFactorPlayers('b')
  }

  get haveXFactor () {
    return this.haveTeamAXFactor || this.haveTeamBXFactor
  }

  haveXFactorPlayers (teamI) {
    const team = this.teams[teamI]
    let players = []
    if (team.match && team.match.x_factor_substitutes) {
      players = team.match.x_factor_substitutes
      if (players && players.length > 0) {
        return true
      }
    }
    return false
  }

  haveTeamPlayers (teamI, usePlayingXI) {
    const team = this.teams[teamI]
    let players = []

    if (usePlayingXI) {
      players = team.match.playing_xi
    } else {
      players = team.match.players
    }

    if (
      players &&
      players instanceof Array &&
      players.length > 0) {
      return true
    }

    return false
  }

  isFollowOn (teaminn) {
    let lastInnTeamI = null

    for (let i = 0; i < this.batting_order.length; i++) {
      const teamI = this.batting_order[i][0]
      if (teamI === lastInnTeamI && teaminn === this.batting_order[i]) {
        return true
      } else {
        lastInnTeamI = teamI
      }
    }

    return false
  }

  formatOver (over) {
    return over.replace('.0', '')
  }

  isInningsCompleted (teaminn) {
    const score = this.innings[`${teaminn[0]}_${teaminn[1]}`]

    if (score.wicket === 10) {
      return true
    } else if (!this.format.isTest) {
      const over = parseInt(score.overs)
      if (over === this.standardMatchOvers) {
        return true
      }
    }

    return false
  }

  isBattingInnings (teaminn) {
    if (this.status.isStarted &&
        teaminn[0] === this.now.batting_team &&
        teaminn[1].toString() === this.now.innings &&
        (!this.isInningsCompleted(teaminn))
    ) {
      return true
    }

    return false
  }

  overPrefix (teaminn) {
    const [teamI, inns] = teaminn
    const inn = parseInt(inns)
    const isFirstBatting = (inn === 1 && this.first_batting === teamI)
    const sub = (inn === 1) ? 'st' : 'nd'
    const subHtml = `${inn}<sup>${sub}</sup>`
    const batText = `${subHtml} bat`

    let prefix = ''

    if (this.format.isTest) {
      prefix += `${subHtml} inn`

      if (isFirstBatting) {
        prefix += `. ${batText}`
      } else if (this.isFollowOn(teaminn)) {
        prefix += '. Follow-on'
      }
    } else {
      if (isFirstBatting) {
        prefix += batText
      } else if (inn === 'superover') {
        prefix += 'superover'
      }
    }

    if (prefix.length > 0) {
      // if (teamI === 'a') {
      //   prefix += '.'
      // } else {
      //   prefix += '. '
      // }
      prefix = `(${prefix})`
    }

    return prefix
  }

  isSameDay (fordate) {
    return this.dayDiff(fordate) === 0
  }

  dayDiff (fordate) {
    return Utils.diffInCalendarDate(fordate, this.start_at)
  }

  overwrite (updatedMatch) {
    Object.entries(updatedMatch).forEach(obj => Vue.set(this, obj[0], obj[1]))
  }

  teamShortName (teamIndexkey) {
    return this.teams[teamIndexkey].short_name
  }

  updateLatest (useFresh, isMetric) {
    let url
    if (isMetric) {
      url = `/cricket/v4/match/${this.key}/`
    } else {
      url = `/cricket/match/${this.key}/`
    }
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(url, params).then((resp) => {
      const updatedMatch = CricketMatch.createFromJson(resp.data.data.match)
      this.overwrite(updatedMatch)
    })
  }

  groupPlayersByRole (playerKeys, players, keeperKey) {
    let playerRoles = []
    let roleMap = {}
    CricketPlayerRoleOrder.forEach((role, index) => {
      playerRoles.push({ name: role.name, players: [] })
      if (role.keys === null) {
        roleMap['others'] = index
      } else if (role.keys === 'not_found') {
        roleMap['not_found'] = index
      } else {
        role.keys.forEach((key) => { roleMap[key] = index })
      }
    })
    playerKeys.forEach((playerKey) => {
      const player = players[playerKey]
      if (player) {
        let role = player.groupingRole
        let index = roleMap['others']
        if (keeperKey) {
          if (player.key === keeperKey) {
            role = CricketPlayerRole.Keeper
          } else if (role === CricketPlayerRole.Keeper) {
            role = CricketPlayerRole.Batsman
          }
        }
        if (roleMap.hasOwnProperty(role.key)) {
          index = roleMap[role.key]
        }
        playerRoles[index].players.push(player.key)
      } else {
        let index = roleMap['not_found']
        playerRoles[index].players.push(playerKey)
      }
    })
    return playerRoles
  }

  fantasyPlayersByRole (players, teamKey) {
    let playerList = []
    Object.keys(players).forEach((player) => {
      if (players[player].team_key == teamKey) {
        playerList.push(player)
      }
    })
    return this.groupPlayersByRole(playerList, players)
  }

  xFactorSubstituteByRole (teamKey) {
    let keeperKey = null
    let playerList = []
    keeperKey = this.teams[teamKey].match.keeper
    playerList = this.teams[teamKey].match.x_factor_substitutes
    if (!this.teams[teamKey].match.x_factor_substitutes) {
      playerList = []
    }

    return this.groupPlayersByRole(playerList, this.players, keeperKey)
  }

  teamPlayersByRole (teamKey, usePlayingXI) {
    let keeperKey = null
    let playerList = []
    if (usePlayingXI === true) {
      keeperKey = this.teams[teamKey].match.keeper
      playerList = this.teams[teamKey].match.playing_xi
      if (!this.teams[teamKey].match.playing_xi) {
        playerList = []
      }
    } else {
      playerList = this.teams[teamKey].match.players
    }

    return this.groupPlayersByRole(playerList, this.players, keeperKey)

    // let playerRoles = []
    // let roleMap = {}
    // CricketPlayerRoleOrder.forEach((role, index) => {
    //   playerRoles.push({ name: role.name, players: [] })
    //   if (role.keys === null) {
    //     roleMap['others'] = index
    //   } else {
    //     role.keys.forEach((key) => { roleMap[key] = index })
    //   }
    // })

    // playerList.forEach((playerKey) => {
    //   const player = this.players[playerKey]
    //   let role = player.role
    //   let index = roleMap['others']

    //   if (usePlayingXI) {
    //     if (player.key === this.teams[teamKey].match.keeper) {
    //       role = CricketPlayerRole.Keeper
    //     } else if (role === CricketPlayerRole.Keeper) {
    //       role = CricketPlayerRole.Batsman
    //     }
    //   }

    //   if (roleMap.hasOwnProperty(role.key)) {
    //     index = roleMap[role.key]
    //   }
    //   playerRoles[index].players.push(player.key)
    // })

    // return playerRoles
  }

  static createFromJson (raw) {
    let match = new CricketMatch()
    match = Object.assign(match, raw)
    match.start_at = moment(match.start_date.iso)
    match.approxCompletedTime = null
    if (match.approx_completed_ts) {
      match.approxCompletedTime = moment.unix(match.approx_completed_ts)
    }
    match.slotTime = match.start_at
    match.status = CricketMatchStatusKind.parse(match.status)
    match.format = CricketMatchFormat.parse(match.format)
    match.day = match.dayDiff(moment())

    if (match.approxCompletedTime) {
      match.day = match.dayDiff(match.approxCompletedTime) + 1
    } else if (match.status.isNotStarted) {
      match.day = 0
    }

    if (match.players) {
      Object.keys(match.players).forEach((key) => {
        match.players[key] = CricketPlayer.createFromJson(key, match.players[key])
      })
    }

    if (match.innings) {
      Object.keys(match.innings).forEach((key) => {
        const partnerships = match.innings[key].partnerships
        if (partnerships) {
          match.innings[key].partnerships = CricketMatchPartnership.createFromList(key, partnerships)
        }
      })
    }

    if (match.now && match.now.recent_overs) {
      match.now.recent_overs.forEach((overBalls) => {
        if (overBalls.length > 1) {
          overBalls[1] = overBalls[1].reverse()
        }
      })
    }

    CricketMatch.fixFirebaseData(match)

    return match
  }

  static fixFirebaseData (match) {
    // Firebase remove null, [], {} objects. This affects some part of the code.
    // Here fix all those objects.

    // Fix innings objects
    if (match.innings) {
      Object.keys(match.innings).forEach((key) => {
        if (!match.innings[key].partnerships) {
          match.innings[key].partnerships = []
        }

        if (!match.innings[key].wicket_order) {
          match.innings[key].wicket_order = []
        }

        if (!match.innings[key].batting) {
          match.innings[key].batting = {}
        }
        if (!match.innings[key].bowling) {
          match.innings[key].bowling = {}
        }
      })
    }

    if (match.players) {
      Object.keys(match.players).forEach((key) => {
        if (match.players[key].match && match.players[key].match.innings) {
          const inns = match.players[key].match.innings
          Object.keys(inns).forEach((key) => {
            const inn = inns[key]
            if (!inn.batting) {
              inn.batting = {}
            }
            if (!inn.bowling) {
              inn.bowling = {}
            }
            if (!inn.fielding) {
              inn.fielding = {}
            }
          })
        }
      })
    }
  }

  fixOvers (overs) {
    for (let i = 2; i < this.batting_order.length; i++) {
      const [teamI, inn] = this.batting_order[i]
      const innOvers = overs[`${teamI}_${inn}`]
      const prevInn = this.innings[`${teamI}_1`]

      innOvers.forEach((over) => {
        over.match.runs -= prevInn.runs
        over.match.wicket -= prevInn.wickets
        over.match.current_run_rate = (over.match.runs / over.over).toFixed(2)
      })
    }

    Object.keys(overs).forEach((key) => {
      const innOver = overs[key]
      let playerStats = {}

      innOver.forEach((over) => {
        over.sixes = 0
        over.fours = 0

        over.batsman.forEach((batsman) => {
          if (!playerStats[batsman.key]) {
            playerStats[batsman.key] = { sixes: 0, fours: 0 }
          }

          const thisPlayerStats = playerStats[batsman.key]
          over.sixes += batsman.batting.sixes - thisPlayerStats.sixes
          over.fours += batsman.batting.fours - thisPlayerStats.fours

          // console.log(over.over, over.sixes, over.fours)

          thisPlayerStats.sixes = batsman.batting.sixes
          thisPlayerStats.fours = batsman.batting.fours
        })
      })
    })
  }

  fetchBalls (overKey, useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    let url = `/cricket/match/${this.key}/balls/`
    if (overKey) {
      url = `${url}${overKey}/`
    }

    return API.instance().get(url, params).then((resp) => {
      const rawData = resp.data.data.balls
      let balls = []

      rawData.over.balls.forEach((ballKey) => {
        const ballRaw = rawData.balls[ballKey]
        if (ballRaw) {
          ballRaw.key = ballKey
          balls.push(ballRaw)
        } else {
          console.error('Ball missing', overKey, ballKey)
        }
      })

      return balls
    })
  }

  fetchOvers (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(`/cricket/match/${this.key}/overs/`, params).then((resp) => {
      const innings = resp.data.data.overs.innings
      console.log('over summary innings', innings)
      let overs = {}
      Object.keys(innings).forEach((k) => {
        overs[k] = innings[k].overs_summary.map((raw) => CricketMatchOverSummary.createFromJson(raw))
      })
      this.fixOvers(overs)
      console.log('overs', overs)
      return overs
    })
  }

  fantasyCredits (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }
    return API.instance().get(`/cricket/fantasy-match-credit/${this.key}/`, params).then((resp) => {
      return this.parseCreditResponse(resp)
    })
  }

  fantasyPoints (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }
    return API.instance().get(`/cricket/fantasy-match-points/${this.key}/`, params).then((resp) => {
      const fantasy = resp.data.data.fantasy
      if (fantasy.players) {
        let players = fantasy.players
        let playerKeys = []

        Object.keys(players).forEach((key) => {
          players[key] = CricketPlayer.createFromJson(key, players[key])
        })

        let points = fantasy.fantasy
        points.forEach((point) => {
          if (players[point.player]) {
            playerKeys.push(point.player)
            players[point.player].fantasyPoints = CricketPlayerFantasyPoint.createFromJson(point)
          } else {
            console.log('Player missing', point.player)
          }
        })

        const grouped = this.groupPlayersByRole(playerKeys, players)
        const teams = fantasy.teams

        return { grouped, players, playerKeys, teams }
      }

      return null
    })
  }

  parseCreditResponse (resp) {
    const fantasy = resp.data.data.fantasy
    if (fantasy.players) {
      let players = fantasy.players
      let playerKeys = []

      Object.keys(players).forEach((key) => {
        players[key] = CricketPlayer.createFromJson(key, players[key])
      })

      let points = fantasy.fantasy_points
      points.forEach((point) => {
        if (players[point.player]) {
          playerKeys.push(point.player)
          players[point.player].fantasyCredit = CricketPlayerFantasyCredit.createFromJson(point)
        } else {
          console.log('Player missing', point.player)
        }
      })

      const grouped = this.groupPlayersByRole(playerKeys, players)
      const teams = fantasy.teams
      const variation = resp.data.data.variation
      return { grouped, players, playerKeys, teams, variation }
    }

    return null
  }

  creditVariations (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader(true)
    }
    return API.instance().get(`/workflow/fantasy-credit-variation/${this.key}/?sport=cricket`, params).then((resp) => {
      return this.parseCreditResponse(resp)
    })
  }
  save (key, players, isFrozen) {
    let frozen = null
    if (isFrozen) {
      frozen = isFrozen
    }
    const postData = {
      payload: players,
      mark_frozen: frozen
    }
    return API.instance().post(`/workflow/fantasy-credit-variation/${key}/?sport=cricket`, postData).then((resp) => {
      return this.parseCreditResponse(resp)
    })
  }

  static fetch (key, useFresh, isMetric) {
    let url
    if (isMetric) {
      url = `/cricket/v4/match/${key}/`
    } else {
      url = `/cricket/match/${key}/`
    }
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(url, params).then((resp) => {
      return CricketMatch.createFromJson(resp.data.data.match)
    })
  }
}
